import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface SearchBoxProps {
  placeholder?: string;
  onSearch?: (searchTerm: string) => void;
  onClear?: () => void;
  className?: string;
  autoFocus?: boolean;
}

export default function SearchBox({
  placeholder,
  onSearch,
  onClear,
  className = "",
  autoFocus = true,
}: SearchBoxProps) {
  const { t } = useTranslation("job-details");
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const defaultPlaceholder = placeholder || t("jobDetails.searchBox.placeholder");

  // Handle click outside to close search box
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Auto focus when opened
  useEffect(() => {
    if (isOpen && autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, autoFocus]);

  const handleSearchIconClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen && autoFocus) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleSearchClick = () => {
    console.log("handleSearchClick called", { searchTerm, onSearch });
    if (searchTerm.trim() && onSearch) {
      console.log("Calling onSearch with:", searchTerm);
      onSearch(searchTerm);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    if (onClear) {
      onClear();
    }
    if (onSearch) {
      onSearch("");
    }
    inputRef.current?.focus();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    console.log("Key pressed:", e.key);
    if (e.key === "Enter") {
      console.log("Enter key pressed, calling handleSearchClick");
      handleSearchClick();
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
      if (onClear) {
        onClear();
      }
      if (onSearch) {
        onSearch("");
      }
    }
  };

  return (
    <div ref={containerRef} className={`search-box-container ${className}`}>
      {!isOpen ? (
        <button className="search-icon-btn" onClick={handleSearchIconClick}>
          <i className="fa fa-search"></i>
        </button>
      ) : (
        <div className="search-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            placeholder={defaultPlaceholder}
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            className="search-input"
          />
          <div className="search-input-actions">
            {searchTerm ? (
              <button className="search-clear-btn" onClick={handleClearSearch} title={t("jobDetails.searchBox.clearSearch")}>
                <i className="fa fa-times"></i>
              </button>
            ) : (
              <></>
            )}
            <button
              className="search-close-btn"
              onClick={(e) => {
                console.log("Search button clicked", { searchTerm, hasSearchTerm: !!searchTerm });
                if (searchTerm) {
                  handleSearchClick();
                } else {
                  setIsOpen(false);
                }
              }}
              title={searchTerm ? t("jobDetails.searchBox.search") : t("jobDetails.searchBox.closeSearch")}
            >
              <i className="fa fa-search"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
